import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../../firebase';
import { secureAuthService } from '../secureAuthService';
import { deleteTrustedRecord } from './chatTrustedBrowser';

/**
 * The fresh sign-in step shared by IdentityResetDialog and the settings gate:
 * the full login again — email/password via `POST /api/auth/login` → custom
 * token → `signInWithCustomToken`, or Google via `signInWithPopup` with
 * `prompt: 'select_account'` (and `login`, where honoured). A refreshed ID
 * token does not renew `auth_time`; re-login is the only way to pass the
 * backend's 300-second window.
 */

export const FRESH_SIGN_IN_WINDOW_S = 300;

export function decodeJwtPayload(token: string | null): Record<string, unknown> | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/** `auth_time` (seconds) of the stored ID token, or null when unknown. */
export function getAuthTimeSeconds(): number | null {
  const payload = decodeJwtPayload(secureAuthService.getCurrentToken());
  const authTime = payload?.auth_time;
  return typeof authTime === 'number' ? authTime : null;
}

/** Whether the current session's sign-in is within the backend's 300-second window. */
export function isSignInFresh(nowMs: number = Date.now()): boolean {
  const authTime = getAuthTimeSeconds();
  return authTime != null && nowMs / 1000 - authTime <= FRESH_SIGN_IN_WINDOW_S;
}

export type SignInAgainResult =
  | { status: 'ok' }
  | { status: 'different-account'; uid: string; email: string };

/**
 * Sign in again with email and password. On a uid other than `expectedUid` the
 * other account is signed out again and nothing else changes for the caller.
 */
export async function signInAgainWithPassword(
  expectedUid: string,
  email: string,
  password: string,
): Promise<SignInAgainResult> {
  const response = await secureAuthService.login(email, password);
  if (response.user.uid !== expectedUid) {
    await signOutOtherAccount(expectedUid);
    return { status: 'different-account', uid: response.user.uid, email: response.user.email };
  }
  return { status: 'ok' };
}

export async function signInAgainWithGoogle(expectedUid: string): Promise<SignInAgainResult> {
  const provider = new GoogleAuthProvider();
  provider.addScope('email');
  provider.addScope('profile');
  provider.setCustomParameters({ prompt: 'select_account' });
  const result = await signInWithPopup(auth, provider);
  if (result.user.uid !== expectedUid) {
    await signOutOtherAccount(expectedUid);
    return { status: 'different-account', uid: result.user.uid, email: result.user.email || '' };
  }
  const idToken = await result.user.getIdToken();
  secureAuthService.storeFirebaseAuth(idToken, {
    uid: result.user.uid,
    email: result.user.email || '',
    displayName: result.user.displayName || '',
    photoURL: result.user.photoURL || undefined,
  });
  return { status: 'ok' };
}

/**
 * Sign the other account out and end the session of the researcher who opened
 * the flow. Their trusted-browser record is deleted by uid: after a password
 * re-login `secureAuthService` already holds the OTHER account, so its own
 * logout would delete the wrong record.
 */
async function signOutOtherAccount(expectedUid: string): Promise<void> {
  try {
    await auth.signOut();
  } catch {
    /* the local session is cleared regardless */
  }
  await deleteTrustedRecord(expectedUid);
  await secureAuthService.logout();
}

/** The message shown on the login screen after a re-login as a different account. */
export function differentAccountMessage(expectedEmail: string): string {
  return `You signed in as a different account. Sign in as ${expectedEmail} to replace that account's chat key.`;
}
